import React, { useEffect, useState } from "react"
import { useSelector, shallowEqual, useDispatch, connect } from "react-redux"
import { useLocation } from "react-router-dom"
import { FormControl, ListSubheader, MenuItem, Select } from "@material-ui/core"
import { profileSlice } from "../../../../app/redux/profile/profileSlice"
import { firestore as db, storage } from "../../../../firebase"
import { doc, getDoc, collection, onSnapshot, getFirestore, query, where } from "firebase/firestore"

import { ref, getDownloadURL } from "firebase/storage"
import BusinessIcon from "@material-ui/icons/Business"
import LocationOnIcon from "@material-ui/icons/LocationOn"
import ClearAllIcon from "@material-ui/icons/ClearAll"
import PersonIcon from "@material-ui/icons/Person"
import * as authRedux from "../../../modules/Auth/_redux/authRedux"
import { injectIntl } from "react-intl"
import { ModalProgressBar } from "../../../_partials/ModalProgressBar"
import { fetchVeeaDetails, refreshLicenseDetails } from "../../../_utils/fetchVeeaDetails"

const { actions } = profileSlice

const ACTIVE_SITES = "Active Sites:"
const COMPLETED_PROJECTS = "Completed Projects:"
const SELECT_CUSTOMER = "Select customer"
const SELECT_LOCATION = "Select location"
const ROLES = {
	ORIGINAL_ROLE: "Original Role",
	MSP: "MSP",
	ADMIN: "Admin",
	USER: "User"
}

function HeaderSelections(props) {
	const dispatch = useDispatch()
	const location = useLocation()

	const { userClaims, floorPlans, currentCustomer, currentSite, currentFloorPlan, veeaHubAPIURL } =
		useSelector(
			state => ({
				userClaims: state.auth.claims,
				floorPlans: state.basePage.floorPlans,
				currentCustomer: state.profile.currentCustomer,
				currentSite: state.profile.currentSite,
				currentFloorPlan: state.profile.currentFloorPlan,
				veeaHubAPIURL: state.profile.veeaHubAPIURL
			}),
			shallowEqual
		)

	const [customers, setCustomers] = useState()
	const [selectedCustomer, setSelectedCustomer] = useState(currentCustomer?.id || null)
	const [selectedSite, setSelectedSite] = useState(currentSite?.id || null)
	const [selectedFloorPlan, setSelectedFloorPlan] = useState(currentFloorPlan?.id || null)
	const [isReporting, setIsReporting] = useState(false)

	const [loadingCustomers, setLoadingCustomers] = useState(true)
	const [loadingSites, setLoadingSites] = useState(true)
	const [loadingFloorPlans, setLoadingFloorPlans] = useState(true)

	const useDocument = path => {
		const [data, setData] = useState(null)
		const [loading, setLoading] = useState(true)

		useEffect(() => {
			if (!path) return

			const documentRef = doc(db, path)
			setLoading(true)

			const unsubscribe = onSnapshot(
				documentRef,
				snapshot => {
					if (snapshot) {
						setData({ id: snapshot.id, ...snapshot.data() })
					} else {
						setData(null)
					}
					setLoading(false)
				},
				error => {
					console.error("Error fetching document:", error)
					setData(null)
					setLoading(false)
				}
			)

			return () => unsubscribe()
		}, [path])

		return { data, loading }
	}

	const { data: customer, loading } = useDocument(
		selectedCustomer ? `Customers/${selectedCustomer}` : null
	)

	const getCustomerDoc = async customer => {
		try {
			const customerDoc = doc(db, "Customers", customer.id)
			const docSnap = await getDoc(customerDoc)

			if (!docSnap.exists()) return

			const realCustomer = docSnap.data()

			const newCustomer = {
				...realCustomer,
				id: customer.id,
				sites: realCustomer?.sites?.filter(
					val =>
						!val.disabled &&
						(userClaims.isSuper || (userClaims.sites && userClaims.sites.includes(val.id)))
				)
			}

			return newCustomer
		} catch (error) {
			console.error("Error fetching customer data:", error)
			return null // Return a fallback value
		}
	}

	useEffect(() => {
		const fetchCustomer = async () => {
			if (!customer) {
				dispatch(actions.customerSelected(null))
				return
			}
			try {
				const customerData = await getCustomerDoc(customer)
				dispatch(actions.customerSelected(customerData))
			} catch (error) {
				console.error("Error fetching customer:", error)
			}
		}

		fetchCustomer()
	}, [customer])

	//if it has customers pre select the first one
	useEffect(() => {
		if (!customers) return

		if (
			(customers && !selectedCustomer) ||
			selectedCustomer === null ||
			selectedCustomer === ""
		) {
			const selectedId = customers && customers?.[0]?.id
			setSelectedCustomer(selectedId)
			return
		}
	}, [customers])

	// If there is only one site, auto select that one
	useEffect(() => {
		if (
			selectedSite ||
			!currentCustomer?.sites ||
			currentCustomer?.sites.length === 0 ||
			currentCustomer?.sites.length > 1
		)
			return

		setTimeout(() => {
			//theres a sync issue thats why we need to settimeout
			// in healthcare refactoring this problem his solved
			setSelectedSite(currentCustomer?.sites[0].id)
		}, 500)
	}, [currentCustomer])

	// If there is no floorplan selected, auto select one
	useEffect(() => {
		if (!floorPlans || floorPlans.length === 0) return

		const selectedId = floorPlans[0]?.id

		if (!selectedFloorPlan) {
			setSelectedFloorPlan(selectedId)
			return
		}

		if (selectedFloorPlan && floorPlans.some(val => val.id === selectedFloorPlan)) return

		if (selectedId !== null) {
			setSelectedFloorPlan(selectedId)
			return
		}

		setSelectedFloorPlan(null)
	}, [floorPlans, selectedFloorPlan])

	useEffect(() => {
		if (floorPlans.length > 0 && !selectedFloorPlan) {
			setSelectedFloorPlan(floorPlans[0].id) // Set the first floor plan's ID as default
		}
	}, [floorPlans, selectedFloorPlan])

	useEffect(() => {
		const fetchSiteData = async () => {
			if (!selectedSite) {
				dispatch(actions.siteSelected(null))
				setLoadingSites(false)
				return
			}

			try {
				setLoadingSites(true)
				const siteDoc = doc(db, "Sites", selectedSite)
				const docSnap = await getDoc(siteDoc)

				if (!docSnap) {
					dispatch(actions.siteSelected(null))
					setLoadingSites(false)
					loadDefaultVeeaHubSerialNumber()
					return
				}

				dispatch(actions.siteSelected({ id: docSnap.id, ...docSnap.data() }))
				loadVeeaHubSerialNumber({ id: docSnap.id, ...docSnap.data() })
				setLoadingSites(false)
			} catch (error) {
				console.error("Error fetching data from Firestore:", error)
				dispatch(actions.siteSelected(null))
				setLoadingSites(false)
				loadDefaultVeeaHubSerialNumber()
			}
		}

		fetchSiteData()
	}, [selectedSite])

	useEffect(() => {
		if (!veeaHubAPIURL) return
		async function refreshLicense() {
			const data = await refreshLicenseDetails(veeaHubAPIURL)
			if (data) {
				dispatch(actions.veeaLicenseDetailsRefreshed({ veeaLicenseisValid: data?.valid }))
			}
		}
		refreshLicense()
	}, [veeaHubAPIURL])

	const loadVeeaHubSerialNumber = async siteData => {
		const data = await fetchVeeaDetails(siteData?.id)
		if (data) {
			let resPayload = {
				veeaHubSerialNumber: data?.serial_number,
				veeaHubRedirectUrl: data?.redirect_url,
				veeaExpiryDate: data?.expiry_date,
				veeaisPaid: data?.is_paid_enabled
			}
			dispatch(actions.veeaHubSerialNumberFetched(resPayload))
		} else {
			loadDefaultVeeaHubSerialNumber()
		}
	}

	const loadDefaultVeeaHubSerialNumber = () => {
		dispatch(
			actions.veeaHubSerialNumberFetched({
				veeaHubSerialNumber: null,
				veeaHubRedirectUrl: null,
				veeaExpiryDate: null,
				veeaisPaid: null
			})
		)
	}

	useEffect(() => {
		const fetchFloorPlanData = async () => {
			if (!currentSite || !selectedFloorPlan) {
				dispatch(actions.floorPlanSelected())
				setLoadingFloorPlans(false)
				return
			}

			try {
				setLoadingFloorPlans(true)

				const floorPlanDoc = doc(
					collection(db, "Sites", currentSite.id, "FloorPlans"),
					selectedFloorPlan
				)
				const docSnap = await getDoc(floorPlanDoc)

				if (!docSnap) return

				const floor = docSnap.data()
				let imageUrl = ""

				if (floor.imagePath) {
					try {
						imageUrl = await getDownloadURL(ref(storage, floor.imagePath))
					} catch (error) {
						console.error("Error fetching image URL:", error)
					}
				}

				dispatch(
					actions.floorPlanSelected({
						id: docSnap.id,
						...floor,
						imageUrl
					})
				)
				setLoadingFloorPlans(false)
			} catch (error) {
				setLoadingFloorPlans(false)
				console.error("Error fetching floor plan data:", error)
				// dispatch(actions.floorPlanSelected())
			}
		}

		fetchFloorPlanData()
	}, [currentSite, selectedFloorPlan])

	function getCustomersSnapshot(db, userClaims, setCustomers) {
		try {
			const customersCollection = collection(db, "Customers")
			let customersQuery

			// If profile level is 3, filter by customer IDs
			if (userClaims.profileLvl === 3) {
				// Use "in" to query by customer IDs if profileLvl is 3
				customersQuery = query(
					customersCollection,
					where("tenantID", "in", userClaims.customerIds)
				)
			} else {
				// Default query (for other profile levels)
				customersQuery = query(customersCollection)
			}

			const unsubscribe = onSnapshot(
				customersQuery,
				snapshot => {
					const data = snapshot.docs.map(doc => ({
						...doc.data(),
						id: doc.id
					}))
					setCustomers(data) // Update state with real-time data
					setLoadingCustomers(false)
				},
				error => {
					console.error("Error fetching customers snapshot:", error)
					setCustomers([]) // Fallback to empty list on error
					setLoadingCustomers(false)
				}
			)

			// Return unsubscribe function to clean up listener
			return unsubscribe
		} catch (error) {
			console.error("Error setting up customer snapshot:", error)
			setLoadingCustomers(false)
			return null
		}
	}
	useEffect(() => {
		if (!db || !userClaims) return

		setLoadingCustomers(false)

		const unsubscribe = getCustomersSnapshot(db, userClaims, data => {
			if (userClaims.isSuper) {
				setCustomers(data) // isSuper
			} else if (userClaims?.profileLvl === 2 || userClaims?.profileLvl === 1) {
				// isUser/isAdmin
				setSelectedCustomer(userClaims.customerId)
			} else if (userClaims?.profileLvl === 3) {
				// isMSP
				const filteredData = data.filter(customer =>
					userClaims.customerIds.includes(customer.id)
				)
				setCustomers(filteredData)
			}
			setLoadingCustomers(false)
		})

		setLoadingCustomers(false)

		// Cleanup the subscription on unmount
		return () => {
			if (unsubscribe) unsubscribe()
		}
	}, [db, userClaims])

	useEffect(() => {
		setIsReporting(location.pathname.includes("reporting"))
	}, [location.pathname])

	const onChangeFloorPlanHandler = floorId => {
		const foundedFloorPlan = floorPlans && floorPlans.find(f => f.id === floorId)

		foundedFloorPlan && dispatch(actions.floorPlanSelected(foundedFloorPlan)) // changes floorplan
	}

	return (
		<div className="header-selections">
			{customers && userClaims.profileLvl >= 3 && (
				<div style={{ marginLeft: "10px" }}>
					<BusinessIcon />
					<FormControl size="small" style={{ marginLeft: "5px" }}>
						<Select
							id="select_customer_header_dropdown"
							displayEmpty
							disableUnderline
							value={selectedCustomer || ""}
							onChange={e => {
								if (selectedCustomer === e.target.value) return // Prevent re-execution if no change

								setSelectedCustomer(e.target.value)
								// Make current customer null so that auto site selection does not select from old customer
								dispatch(actions.customerSelected(null))
								setSelectedSite(null)
								setSelectedFloorPlan()
							}}
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 48 * 3 + 8,
										width: 250
									}
								},
								getContentAnchorEl: null,
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left"
								}
							}}
						>
							<MenuItem value="" disabled>
								{SELECT_CUSTOMER}
							</MenuItem>
							{customers.map(val => {
								return (
									<MenuItem key={val.id} value={val.id}>
										{val.customerInfo?.name}
									</MenuItem>
								)
							})}
						</Select>
					</FormControl>
					{loadingCustomers && <ModalProgressBar />}
				</div>
			)}

			{userClaims.profileLvl >= 2 && (
				<div style={{ marginLeft: "10px" }}>
					<LocationOnIcon />
					<FormControl size="small" style={{ marginLeft: "5px" }}>
						<Select
							id="select_site_header_dropdown"
							displayEmpty
							disableUnderline
							value={currentSite?.id || ""}
							onChange={e => {
								let newSiteId = e.target.value
								if (newSiteId && newSiteId === currentSite?.id) return

								if (newSiteId === "") {
									dispatch(actions.siteSelected(null)) // set selectSite to null when clicking on Select Location option
								}

								if (newSiteId === "") {
									dispatch(actions.siteSelected(null))
									setSelectedSite(null)
									return
								}

								setSelectedSite(newSiteId)
							}}
						>
							<MenuItem value="" disabled={currentCustomer?.sites?.length < 2}>
								{SELECT_LOCATION}
							</MenuItem>
							<ListSubheader
								style={{
									fontSize: "13px",
									fontWeight: 500,
									userSelect: "none",
									pointerEvents: "none"
								}}
							>
								{ACTIVE_SITES}
							</ListSubheader>

							{currentCustomer?.sites &&
								currentCustomer.sites.map(val => {
									if (!val.siteFinished) {
										return (
											<MenuItem key={val.id} value={val.id}>
												{val.name}
											</MenuItem>
										)
									}
								})}
							{currentCustomer?.sites &&
								currentCustomer.sites.some(site => site.siteFinished) && (
									<ListSubheader
										style={{
											fontSize: "13px",
											fontWeight: 500,
											userSelect: "none",
											pointerEvents: "none"
										}}
									>
										{COMPLETED_PROJECTS}
									</ListSubheader>
								)}
							{currentCustomer?.sites &&
								currentCustomer.sites.map(val => {
									if (val.siteFinished) {
										return (
											<MenuItem key={val.id} value={val.id}>
												{val.name}
											</MenuItem>
										)
									}
								})}
						</Select>
					</FormControl>
					{loadingSites && <ModalProgressBar />}
				</div>
			)}

			{userClaims.profileLvl >= 1 && !isReporting && (
				<div style={{ marginLeft: "10px" }}>
					<ClearAllIcon />
					<FormControl size="small" style={{ marginLeft: "5px" }}>
						<Select
							id="select_floorplan_header_dropdown"
							displayEmpty
							disableUnderline
							value={
								floorPlans.length > 0 && currentFloorPlan?.id ? currentFloorPlan?.id : ""
							}
							onChange={e => {
								setSelectedFloorPlan(e.target.value)
								onChangeFloorPlanHandler(e.target.value) // need this to match the selectedFloorPlan and currentFloorPlan
							}}
						>
							<MenuItem value="" disabled>
								Select floorplan
							</MenuItem>
							{floorPlans &&
								floorPlans.map((val, i) => {
									return (
										<MenuItem key={i} value={val?.id}>
											{val?.name}
										</MenuItem>
									)
								})}
						</Select>
					</FormControl>
					{loadingFloorPlans && <ModalProgressBar />}
				</div>
			)}

			{userClaims.isSuper && (
				<div style={{ marginLeft: "10px" }}>
					<PersonIcon />
					<FormControl size="small" style={{ marginLeft: "5px" }}>
						<Select
							displayEmpty
							disableUnderline
							value={
								userClaims.profileLvl === 3
									? "msp"
									: userClaims.profileLvl === 2
									? "admin"
									: userClaims.profileLvl === 1
									? "user"
									: ""
							}
							onChange={e => {
								if (!userClaims.isSuper) return
								if (e.target.value === "msp") {
									const claims = {
										...userClaims,
										profileLvl: 3
									}
									props.setUserClaims(claims)
								} else if (e.target.value === "admin") {
									const claims = {
										...userClaims,
										profileLvl: 2
									}
									props.setUserClaims(claims)
								} else if (e.target.value === "user") {
									const claims = {
										...userClaims,
										profileLvl: 1
									}
									props.setUserClaims(claims)
								} else {
									const claims = {
										...userClaims,
										profileLvl: authRedux.getProfileLvl(userClaims)
									}
									props.setUserClaims(claims)
								}
							}}
						>
							<MenuItem value="">{ROLES.ORIGINAL_ROLE}</MenuItem>
							<MenuItem value="msp">{ROLES.MSP}</MenuItem>
							<MenuItem value="admin">{ROLES.ADMIN}</MenuItem>
							<MenuItem value="user">{ROLES.USER}</MenuItem>
						</Select>
					</FormControl>
				</div>
			)}
		</div>
	)
}

export default injectIntl(connect(null, authRedux.actions)(HeaderSelections))
